// Downloaded report file names (FIG-345 · UC-232, REQ-052; DECIDED 20 Oct):
//   YYYYMMDD_[merchant]_ReportName.csv, dated to the day the data runs to.
// The Merchant acronym (REQ-052) doesn't exist yet, so the merchant part is a
// cleaned-up organisation name; switch to the acronym once it is built.
import { TEST_NAME_PREFIX } from './merchantStatus.ts';
import { dateInTimeZone } from './ptDates.ts';

const REPORT_FILE_NAMES: Record<string, string> = {
  skus: 'Variants',
  'sales-history': 'SalesHistory',
  'fixed-costs': 'FixedCosts',
};

/**
 * Clean-up rule (DECIDED 20 Oct): remove the [TEST] prefix; keep letters and
 * numbers; turn spaces and runs of other characters into one underscore; trim
 * underscores from the ends. "Quill & Co." → "Quill_Co".
 */
export function cleanMerchantFileName(name: string | null | undefined): string {
  const withoutTest = (name ?? '').trimStart().startsWith(TEST_NAME_PREFIX)
    ? (name ?? '').trimStart().slice(TEST_NAME_PREFIX.length)
    : (name ?? '');
  const cleaned = withoutTest.replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return cleaned || 'Merchant';
}

/** The longest optional label a user can append to a report file name (FIG-333). */
export const MAX_FILE_LABEL_LENGTH = 40;

const isSeparator = (ch: string) => ch === '_' || ch === '-';

function trimSeparators(value: string): string {
  // Index scan instead of /^[_-]+|[_-]+$/g: a stored label is untrusted and can be very long.
  let start = 0;
  let end = value.length;
  while (start < end && isSeparator(value[start])) start += 1;
  while (end > start && isSeparator(value[end - 1])) end -= 1;
  return value.slice(start, end);
}

/**
 * The optional label users append to a report file name (FIG-333): ASCII letters, digits and `-`
 * are kept, every run of other characters (spaces, dots, slashes, accents, control characters)
 * becomes one `_`, `_` and `-` are trimmed from both ends, the result is cut to 40 characters and
 * trimmed again. An empty result means "no label". Safe by construction: the output can never hold
 * a path separator, a dot or a control character. Idempotent, and run again wherever a stored
 * label is read, because the stored JSON is not trusted.
 */
export function cleanFileLabel(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const cleaned = trimSeparators(raw.replace(/[^A-Za-z0-9-]+/g, '_'));
  return trimSeparators(cleaned.slice(0, MAX_FILE_LABEL_LENGTH));
}

/**
 * YYYYMMDD of a date. A 'YYYY-MM-DD' string is a calendar date already (an as-of or snapshot
 * date) and is used as it is; a Date is an instant, read as the calendar day in Pacific time
 * (FIG-429), so a file made at 22:00 PDT is not dated the next UTC day.
 */
export function fileDateStamp(date: Date | string): string {
  const ymd = typeof date === 'string' ? date.slice(0, 10) : dateInTimeZone(date);
  return ymd.replace(/-/g, '');
}

export function reportFileName(options: {
  merchantName: string | null | undefined;
  reportType: string | null | undefined;
  /** The day the data runs to: a 'YYYY-MM-DD' date, or an instant (read as its PT calendar day). */
  dataDate: Date | string;
  /** Optional user label, appended as `_<label>` after the report name (FIG-333). Cleaned here too. */
  label?: string | null;
}): string {
  const report = REPORT_FILE_NAMES[options.reportType ?? ''] ?? 'Report';
  const label = cleanFileLabel(options.label);
  return `${fileDateStamp(options.dataDate)}_${cleanMerchantFileName(options.merchantName)}_${report}${label ? `_${label}` : ''}.csv`;
}
